<template>
  <div class="comp-score-tab">
    <div class="comp-score-tab__container">
      <!-- Left Section: Score Controls -->
      <div class="comp-score-tab__controls">
        <!-- Header with Scenario and Score -->
        <div class="comp-score-tab__header">
          <div class="comp-score-tab__scenario-section">
            <label class="comp-score-tab__label">SCENARIO</label>
            <div class="comp-score-tab__scenario-controls">
              <div class="comp-score-tab__scenario-badge">
                {{ selectedScenarioName }}
              </div>
              
              <div class="themed-dropdown">
                <button
                  @click="toggleSettings"
                  class="comp-score-tab__settings-button"
                  aria-haspopup="true"
                  :aria-expanded="settingsOpen"
                >
                  <theme-icon icon="settings" type="icons" classes="w-5 h-5" />
                </button>

                <div v-if="settingsOpen" class="themed-dropdown__panel" role="menu">
                  <div class="themed-dropdown__section">
                    <h3 class="comp-score-tab__settings-title">Scoring Options</h3>
                    <div class="comp-score-tab__settings-options">
                      <div v-if="hasPrice" class="comp-score-tab__settings-option">
                        <span class="comp-score-tab__settings-label">Use Price</span>
                        <themed-toggle v-model="usePrice" @input="calculateCompScore" />
                      </div>
                    </div>
                  </div>
                  
                  <div class="themed-dropdown__actions">
                    <themed-button
                      @click="toggleSettings"
                      variant="default"
                      size="sm"
                    >
                      Close
                    </themed-button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="comp-score-tab__score-badge" :class="scoreColorClass">
            {{ compScore }}
          </div>
        </div>

        <!-- Drivers Table -->
        <div class="comp-score-tab__drivers">
          <div v-if="scenarioKsds.length" class="comp-score-tab__table">
            <div class="comp-score-tab__table-header">
              <div class="comp-score-tab__table-cell comp-score-tab__table-cell--driver">DRIVER</div>
              <div class="comp-score-tab__table-cell comp-score-tab__table-cell--range">
                <div class="comp-score-tab__range-labels">
                  <span>POOR</span>
                  <span>PERFECT</span>
                </div>
              </div>
              <div class="comp-score-tab__table-cell comp-score-tab__table-cell--weight">WEIGHT (%)</div>
            </div>

            <div class="comp-score-tab__table-body">
              <div
                v-for="ksd in scenarioKsds"
                :key="ksd.id"
                class="comp-score-tab__table-row"
              >
                <div class="comp-score-tab__table-cell comp-score-tab__table-cell--driver">
                  {{ ksd.driver }}
                </div>
                <div class="comp-score-tab__table-cell comp-score-tab__table-cell--range">
                  <input
                    v-if="ksd.driver != 'Price'"
                    v-model.number="ksd.score"
                    @input="updateSliderProgress($event, ksd)"
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    class="comp-score-tab__slider"
                    :style="getSliderStyle(ksd.score)"
                  />
                  <input
                    v-else
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    disabled
                    class="comp-score-tab__slider comp-score-tab__slider--disabled"
                  />
                </div>
                <div class="comp-score-tab__table-cell comp-score-tab__table-cell--weight">
                  {{ ksd.weight }}%
                </div>
              </div>
            </div>

            <div class="comp-score-tab__table-footer">
              <div class="comp-score-tab__table-cell comp-score-tab__table-cell--driver">
                UNALLOCATED PERCENTAGE
              </div>
              <div class="comp-score-tab__table-cell comp-score-tab__table-cell--range"></div>
              <div class="comp-score-tab__table-cell comp-score-tab__table-cell--weight">
                {{ unallocatedPercentage }}%
              </div>
            </div>
          </div>

          <div v-else class="comp-score-tab__empty">
            <p>No KSDs Found</p>
          </div>

          <div v-if="scenarioKsds.length" class="comp-score-tab__actions">
            <themed-button @click="saveKSDScores" variant="default">
              Save
            </themed-button>
          </div>
        </div>
      </div>

      <!-- Right Section: Chart and Legend -->
      <div v-if="showChart && scenarioKsds.length" class="comp-score-tab__visualization">
        <div class="comp-score-tab__chart">
          <theme-pie-chart
            :key="chartKey"
            :data="chartData"
            :show-percentages="true"
          />
        </div>

        <div class="comp-score-tab__legend">
          <div
            v-for="(item, index) in chartData"
            :key="`legend-${index}-${item.value}`"
            class="comp-score-tab__legend-item"
          >
            <div
              class="comp-score-tab__legend-color"
              :style="{ backgroundColor: getColorForIndex(index) }"
            ></div>
            <span class="comp-score-tab__legend-label">
              {{ item.value }}% {{ item.label }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ThemedButton from '../ThemedButton.vue';
import ThemedToggle from '../ThemedToggle.vue';
import ThemeIcon from '../ThemeIcon.vue';
import ThemePieChart from '../charts/ThemePieChart.vue';

export default {
  name: 'CompScoreTab',

  components: {
    ThemedButton,
    ThemedToggle,
    ThemeIcon,
    ThemePieChart
  },

  props: {
    projectId: {
      type: Number,
      required: true
    },

    scorableId: {
      type: Number,
      required: true
    },

    scorableType: {
      type: String,
      required: true
    },

    data: {
      type: [Array, Object],
      required: true
    },

    isModal: {
      type: Boolean,
      default: false
    },

    showChart: {
      type: Boolean,
      default: true
    },

    fromReatlas: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      usePrice: false,
      useFavoritesOnly: false,
      compScore: 0,
      chartKey: 0,
      settingsOpen: false
    };
  },

  created() {
    // get ksd scores for current comp
    this.setKSDScores();

    // calculate comp score
    this.calculateCompScore();
  },

  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  watch: {
    scorableId() {
      this.setKSDScores();
      this.calculateCompScore();
      this.chartKey++;
    },

    'ksds.selectedScenarioId'() {
      this.setKSDScores();
      this.calculateCompScore();
      this.chartKey++;
    },

    chartData: {
      handler() {
        // Force chart re-render when chart data changes
        this.chartKey++;
      },
      deep: true
    }
  },

  computed: {
    ...mapState(['projects', 'ksds', 'buildings']),
    ...mapGetters(['scenario', 'scenarioKsds']),

    project() {
      return this.projects.selectedProject;
    },

    projectUnits() {
      return _.flatMap(this.project.comps, comp => comp.units);
    },

    comp() {
      return this.buildings.selectedBuilding;
    },

    minProjectUnitRent() {
      return _.minBy(this.projectUnits, function(unit) {
        return unit.rent_cost;
      })['rent_cost'] || 0;
    },

    maxProjectUnitRent() {
      return _.maxBy(this.projectUnits, function(unit) {
        return unit.rent_cost;
      })['rent_cost'] || 0;
    },

    scenarioOptions() {
      if (!this.ksds.scenarios || !Array.isArray(this.ksds.scenarios)) {
        return [];
      }
      return this.ksds.scenarios.map(scenario => ({
        value: scenario.id,
        label: scenario.name
      }));
    },

    selectedScenarioName() {
      if (!this.ksds.scenarios || !Array.isArray(this.ksds.scenarios)) {
        return 'Default';
      }
      const selected = this.ksds.scenarios.find(s => s.id === this.ksds.selectedScenarioId);
      return selected ? selected.name : 'Default';
    },

    chartData() {
      return _.map(this.scenarioKsds, ksd => {
        return { label: ksd.driver, value: ksd.weight };
      });
    },

    unallocatedPercentage() {
      return (
        100 -
        parseInt(
          _.sumBy(this.scenarioKsds, ksd => {
            return parseInt(ksd.weight);
          })
        )
      );
    },

    scoreColorClass() {
      if (this.compScore < 70) {
        return 'comp-score-tab__score-badge--low';
      } else if (this.compScore >= 90) {
        return 'comp-score-tab__score-badge--high';
      } else {
        return 'comp-score-tab__score-badge--medium';
      }
    },

    isDefaultScenario() {
      return this.scenario.is_default === 1;
    },

    hasPrice() {
      return _.find(this.scenarioKsds, ['driver', 'Price']) != undefined;
    }
  },

  methods: {
    toggleSettings() {
      this.settingsOpen = !this.settingsOpen;
    },

    handleClickOutside(event) {
      const target = event.target;
      const isDropdown = target.closest('.themed-dropdown');
      
      if (!isDropdown) {
        this.settingsOpen = false;
      }
    },

    updateSliderProgress(event, ksd) {
      this.calculateCompScore();
    },

    getSliderStyle(score) {
      const percentage = (score / 5) * 100;
      return {
        background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${percentage}%, var(--border) ${percentage}%, var(--border) 100%)`
      };
    },

    setKSDScores() {
      // get ksd scores for current comp
      _.map(this.scenarioKsds, ksd => {
        const comp = _.find(ksd.scores, {
          scorable_id: this.scorableId,
          scorable_type: this.scorableType
        });
        ksd.score = comp ? comp.score : 0;
      });
    },

    calculateCompScore() {
      var compScore = 0;

      const priceKSD = _.find(this.scenarioKsds, ['driver', 'Price']);
      const priceWeight = priceKSD ? parseInt(priceKSD.weight) : 0;

      _.map(this.scenarioKsds, ksd => {
        const score = ksd.score ? parseInt(ksd.score) : 0;
        const weight = parseInt(ksd.weight);
        const divisble = this.usePrice ? 100 : 100 - priceWeight;

        const scorePercentage = score * 20;
        const weightedScore = (scorePercentage * weight) / divisble;

        compScore += weightedScore;
      });

      if (this.usePrice) {
        let buildingAvgUnitPrice = this.comp.avg_rent;

        let priceDelta =
          (this.maxProjectUnitRent - this.minProjectUnitRent) / 100;
        let priceScore =
          priceDelta == 0
            ? 0
            : ((this.maxProjectUnitRent - buildingAvgUnitPrice) / priceDelta *
                priceWeight) /
              100;

        priceScore = buildingAvgUnitPrice == 0 ? 0 : priceScore;

        compScore += parseFloat(priceScore);
      }

      compScore = Math.round(compScore);

      this.compScore = compScore > 100 ? 100 : compScore;

      this.$emit('comp-score-updated', this.compScore);
    },

    saveKSDScores() {
      // save scores
      var scores = _.map(this.scenarioKsds, ksd => {
        return {
          ksd_id: ksd.id,
          scorable_id: this.scorableId,
          scorable_type: this.scorableType,
          score: ksd.score
        };
      });

      axios
        .patch('/ksds-scores', scores)
        .then(response => {
          this.$notify({
            type: 'success',
            title: 'Success',
            text: response.data.message
          });
        })
        .catch(response => {
          console.error(response);
        });

      // update comp score if default scenario
      if (this.isDefaultScenario || this.ksds.scenarios.length == 1) {
        axios
          .patch(
            '/projects/' + this.projectId + '/comps/' + this.scorableId,
            {
              score: this.compScore
            }
          )
          .then(response => {
            this.$emit('comp-score-updated', this.compScore);

            this.$notify({
              type: 'success',
              title: 'Success',
              text: 'Comp Score Updated'
            });
          })
          .catch(response => {
            console.error(response);
          });
      }
    },

    getColorForIndex(index) {
      const colors = [
        '#16a34a', // green - Price
        '#0ea5e9', // blue - Expandability  
        '#f97316', // orange - Amenities
        '#eab308', // yellow - Distance to Airports
        '#ec4899', // pink - Aesthetic
        '#8b5cf6', // purple - Proximity to Customers
        '#14b8a6', // teal
        '#ef4444', // red
        '#06b6d4', // cyan
        '#f59e0b'  // amber
      ];
      return colors[index % colors.length];
    }
  }
};
</script>
